/* eslint-disable @typescript-eslint/unbound-method */
import { OverlayModule, type ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  resolveSupportedLanguage,
  type SupportedLanguage,
} from '../../../app.i18n';
import { TranslationLanguageContextDirective } from '../../shared/directives/translation-language-context.directive';
import * as fromAssistantIcon from '../assistants.icon.helper';
import { AssistantLogoDropdownComponent } from './assistant-builder-logo-dropdown.component';
import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  INTRO_MESSAGE_MAX_LENGTH,
  INTRO_MESSAGE_MIN_LENGTH,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  getFormErrorMessage,
  grandfatheredDescriptionMaxLength,
  minLengthWhenNotEmpty,
} from './assistant-builder.validators';
import { TrimOnBlurDirective } from './trim-on-blur.directive';

/**
 * Authoring slots the builder supports. Three INDEPENDENT slots (SB-3325 PR-4 /
 * KD-18, spec §6): `und` is the single-language mode (multilingual OFF); `fr`
 * and `en` are the multilingual mode. Each is its own FormGroup, so toggling
 * multilingual never carries a value between the single-language slot and the
 * fr/en slots.
 */
export type BuilderLocale = 'fr' | 'en' | 'und';

/** Per-locale translatable controls (logo is shared, held once on the component). */
export interface AssistantBuilderBasicForm {
  name: FormControl<string>;
  description: FormControl<string>;
  introMessage: FormControl<string | null>;
}

/** What the page reads back per locale — logo merged in from the shared control. */
export interface AssistantBuilderBasicFormValues {
  name: string;
  logo: string;
  description: string;
  introMessage: string | null;
}

const BUILDER_LOCALES: readonly BuilderLocale[] = ['fr', 'en', 'und'];

let nextUniqueId = 0;

@Component({
  selector: 'squadbox-assistant-builder-basic-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    fromSeriousSystem.InputComponent,
    fromSeriousSystem.TextareaComponent,
    OverlayModule,
    fromSeriousSystem.UseIconDirective,
    fromSeriousSystem.UseArrowIconDirective,
    AssistantLogoDropdownComponent,
    TrimOnBlurDirective,
    TranslationLanguageContextDirective,
  ],
  template: `
    <div class="flex flex-col gap-4">
      <!-- Name row(s) + shared Icon -->
      <div class="flex flex-row justify-between gap-4">
        <div class="grow flex flex-col gap-2">
          @if (isMultilingual()) {
            <p
              [id]="nameLabelId"
              class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5 text-neutral-700"
            >
              {{ 'ASSISTANTS.SAVE_MODAL.FORM.NAME.LABEL' | translate }}
              <span class="text-primary-500"> *</span>
            </p>
          }
          @for (loc of locales(); track loc) {
            @if (isMultilingual()) {
              <!-- SBB-04: each fr/en slot resolves its OWN-language placeholder
                   via a scoped translation context. The shared heading above
                   carries the UI-language label ([label] is '' here), so only the
                   placeholder passes through the scoped context. -->
              <div
                class="grow flex flex-col"
                [squadboxTranslationLanguageContext]="
                  placeholderLanguageFor(loc)
                "
              >
                <sd-input
                  squadboxTrimOnBlur
                  class="grow"
                  [formControl]="forms[loc].controls.name"
                  [leadingLabel]="isMultilingual() ? displayLocaleTag(loc) : ''"
                  [ariaLabelledby]="isMultilingual() ? nameLabelId : ''"
                  [label]="
                    isMultilingual()
                      ? ''
                      : ('ASSISTANTS.SAVE_MODAL.FORM.NAME.LABEL' | translate)
                  "
                  [required]="true"
                  [placeholder]="
                    'ASSISTANTS.SAVE_MODAL.FORM.NAME.PLACEHOLDER' | translate
                  "
                  [limit]="NAME_MAX_LENGTH"
                  [showError]="shouldShowError(forms[loc].controls.name)"
                  [errorMessage]="getErrorMessage(forms[loc].controls.name)"
                ></sd-input>
              </div>
            } @else {
              <sd-input
                squadboxTrimOnBlur
                class="grow"
                [formControl]="forms[loc].controls.name"
                [leadingLabel]="isMultilingual() ? displayLocaleTag(loc) : ''"
                [ariaLabelledby]="isMultilingual() ? nameLabelId : ''"
                [label]="
                  isMultilingual()
                    ? ''
                    : ('ASSISTANTS.SAVE_MODAL.FORM.NAME.LABEL' | translate)
                "
                [required]="true"
                [placeholder]="
                  'ASSISTANTS.SAVE_MODAL.FORM.NAME.PLACEHOLDER' | translate
                "
                [limit]="NAME_MAX_LENGTH"
                [showError]="shouldShowError(forms[loc].controls.name)"
                [errorMessage]="getErrorMessage(forms[loc].controls.name)"
              ></sd-input>
            }
          }
        </div>
        <!-- Form: Icon (shared across locales) — an 80px field with a chevron
             (Figma 25559:21609), opening the icon picker. -->
        <div class="flex flex-col gap-2 relative">
          <p
            [id]="imageLabelId"
            class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5"
          >
            {{ 'ASSISTANTS.SAVE_MODAL.FORM.IMAGE.LABEL' | translate }}
          </p>
          @if (logoControl.value) {
            <button
              type="button"
              class="
              flex items-center justify-between
              w-20 h-12 rounded-lg pl-4 pr-3
              border border-neutral-200
              bg-shades-white hover:border-primary-300
              "
              [attr.aria-labelledby]="imageLabelId"
              aria-haspopup="menu"
              [attr.aria-expanded]="isLogoDropdownVisible()"
              cdkOverlayOrigin
              #logoTrigger="cdkOverlayOrigin"
              #logoTriggerEl
              (click)="handleClickToggleLogoDropdownCTA()"
              (keydown.escape)="handleLogoTriggerEscape($event)"
            >
              @let logo = logoControl.value || '';
              <!-- One dispatcher for every icon set — see
                   assistant-logo.component.ts for why the switch went away. -->
              @let logoDescriptor =
                fromAssistantIcon.getAssistantIconDescriptor(logo);
              <!-- ⚠️ text-primary-300, NOT text-primary-500/50. Both land on a
                   pale blue and they are not the same one: primary-300 is
                   #87a5fc, primary-500 at 50% over white is #9598ff. This field
                   sits 6px from the icon panel, which draws its glyphs with
                   Figma's own faded-icon token — get_variable_defs on the Skill
                   Builder frame 25459:65782 returns exactly one,
                   Icon/icon-primary-faded #87a5fc, and no half-alpha primary at
                   all. The /50 here predates that token and only matched while
                   the old dropdown used it too; T9 replaced that dropdown, so
                   keeping it would show the same glyph in two colors side by
                   side. Reported from the running app by @maxeber. -->
              <!-- ⚠️ Every child here is pointer-events-none, so the BUTTON is
                   the only hit target. Without it the whole 80x48 field looks
                   clickable but only parts of it behave that way: a press
                   landing on the glyph or the chevron makes that child the
                   event target, and the two children do not behave alike. The
                   chevron is a thin V whose 24px box is nearly all transparent,
                   so a press there usually falls through to the button; the
                   -filled glyphs are dense, so a press there almost always
                   lands on the icon itself. Reported by @maxeber as "clicking
                   the icon does nothing, clicking the arrow opens it".

                   The teammate picker never had this: its trigger's content is
                   a text node, and text nodes are not event targets, so its
                   button is already the only thing that can be pressed
                   (admin-teammate-builder-modal.component.ts). This makes the
                   Skill Builder's trigger the same shape.

                   It also stops the <img> branch from swallowing the press
                   outright: images are draggable by default, and a press that
                   drifts a few pixels before release starts a native drag and
                   fires NO click at all. -->
              @if (logoDescriptor) {
                <svg
                  [sdUseIcon]="logoDescriptor"
                  class="size-6 shrink-0 pointer-events-none text-primary-300"
                ></svg>
              } @else if (logo) {
                <img
                  class="size-6 shrink-0 pointer-events-none"
                  draggable="false"
                  [src]="logo"
                  [alt]="'CHAT.SKILL_LOGO_ALT' | translate"
                />
              }
              <svg
                [sdUseArrowIcon]="'chevron-small-down'"
                class="size-6 shrink-0 pointer-events-none text-neutral-400"
              ></svg>
            </button>
            <!-- ⚠️ Rendered in a CDK overlay (body-level), NOT absolutely inside
                 this column, and that is not a style choice.

                 The panel is 376px wide and this form column carries
                 tablet-landscape:overflow-x-hidden. Measured in the browser
                 2026-09-02: the column is 336px at a 1024px window and 753px at
                 1441px — so BELOW roughly 1100px the panel is wider than the box
                 that clips it, and NO anchoring fits. Right-anchored it lost its
                 left edge, centred it lost 148px on the right, both silently:
                 no scrollbar, no error, just missing icon columns.

                 The teammate picker reached the same conclusion first
                 (admin-teammate-builder-modal.component.ts). Floating over the
                 form also gives Escape-to-close for free, which is why #1450 is
                 closed by this. -->
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="logoTrigger"
              [cdkConnectedOverlayOpen]="isLogoDropdownVisible()"
              [cdkConnectedOverlayPositions]="logoOverlayPositions"
              [cdkConnectedOverlayPush]="true"
              (overlayKeydown)="handleLogoOverlayKeydown($event, logoTriggerEl)"
              (overlayOutsideClick)="isLogoDropdownVisible.set(false)"
              (detach)="isLogoDropdownVisible.set(false)"
            >
              <squadbox-assistant-builder-logo-dropdown
                [selected]="logo"
                (logoClicked)="handleLogoClick($event)"
              ></squadbox-assistant-builder-logo-dropdown>
            </ng-template>
          }
        </div>
      </div>

      <!-- Description row(s) -->
      <div class="flex flex-col gap-2">
        @if (isMultilingual()) {
          <p
            [id]="descriptionLabelId"
            class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5 text-neutral-700"
          >
            {{ 'ASSISTANTS.SAVE_MODAL.FORM.DESCRIPTION.LABEL' | translate }}
            <span class="text-primary-500"> *</span>
          </p>
        }
        @for (loc of locales(); track loc) {
          @if (isMultilingual()) {
            <!-- SBB-04: per-slot own-language placeholder (see Name field). -->
            <div
              class="flex flex-col"
              [squadboxTranslationLanguageContext]="placeholderLanguageFor(loc)"
            >
              <sd-input
                squadboxTrimOnBlur
                [formControl]="forms[loc].controls.description"
                [leadingLabel]="isMultilingual() ? displayLocaleTag(loc) : ''"
                [ariaLabelledby]="isMultilingual() ? descriptionLabelId : ''"
                [label]="
                  isMultilingual()
                    ? ''
                    : ('ASSISTANTS.SAVE_MODAL.FORM.DESCRIPTION.LABEL'
                      | translate)
                "
                [required]="true"
                [placeholder]="
                  'ASSISTANTS.SAVE_MODAL.FORM.DESCRIPTION.PLACEHOLDER'
                    | translate
                "
                [limit]="descriptionMaxFor(loc)"
                [showError]="shouldShowError(forms[loc].controls.description)"
                [errorMessage]="
                  getErrorMessage(forms[loc].controls.description)
                "
              ></sd-input>
            </div>
          } @else {
            <sd-input
              squadboxTrimOnBlur
              [formControl]="forms[loc].controls.description"
              [leadingLabel]="isMultilingual() ? displayLocaleTag(loc) : ''"
              [ariaLabelledby]="isMultilingual() ? descriptionLabelId : ''"
              [label]="
                isMultilingual()
                  ? ''
                  : ('ASSISTANTS.SAVE_MODAL.FORM.DESCRIPTION.LABEL' | translate)
              "
              [required]="true"
              [placeholder]="
                'ASSISTANTS.SAVE_MODAL.FORM.DESCRIPTION.PLACEHOLDER' | translate
              "
              [limit]="descriptionMaxFor(loc)"
              [showError]="shouldShowError(forms[loc].controls.description)"
              [errorMessage]="getErrorMessage(forms[loc].controls.description)"
            ></sd-input>
          }
        }
      </div>

      <!-- Intro Message row(s) -->
      @if (hasIntroMessage()) {
        <div class="flex flex-col gap-2">
          @if (isMultilingual()) {
            <p
              [id]="introLabelId"
              class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5 text-neutral-700"
            >
              {{ 'ASSISTANTS.SAVE_MODAL.FORM.INTRO_MESSAGE.LABEL' | translate }}
              @if (isCreateMode()) {
                <span class="text-primary-500"> *</span>
              }
            </p>
          }
          @for (loc of locales(); track loc) {
            @if (isMultilingual()) {
              <!-- SBB-04: per-slot own-language placeholder (see Name field). -->
              <div
                class="flex flex-col"
                [squadboxTranslationLanguageContext]="
                  placeholderLanguageFor(loc)
                "
              >
                <sd-textarea
                  squadboxTrimOnBlur
                  [formControl]="forms[loc].controls.introMessage"
                  [leadingLabel]="isMultilingual() ? displayLocaleTag(loc) : ''"
                  [ariaLabelledby]="isMultilingual() ? introLabelId : ''"
                  [label]="
                    isMultilingual()
                      ? ''
                      : ('ASSISTANTS.SAVE_MODAL.FORM.INTRO_MESSAGE.LABEL'
                        | translate)
                  "
                  [required]="isCreateMode()"
                  [placeholder]="
                    'ASSISTANTS.SAVE_MODAL.FORM.INTRO_MESSAGE.PLACEHOLDER'
                      | translate
                  "
                  [limit]="INTRO_MESSAGE_MAX_LENGTH"
                  [showCounter]="false"
                  [showError]="
                    shouldShowError(forms[loc].controls.introMessage)
                  "
                  [errorMessage]="
                    getErrorMessage(forms[loc].controls.introMessage)
                  "
                ></sd-textarea>
              </div>
            } @else {
              <sd-textarea
                squadboxTrimOnBlur
                [formControl]="forms[loc].controls.introMessage"
                [leadingLabel]="isMultilingual() ? displayLocaleTag(loc) : ''"
                [ariaLabelledby]="isMultilingual() ? introLabelId : ''"
                [label]="
                  isMultilingual()
                    ? ''
                    : ('ASSISTANTS.SAVE_MODAL.FORM.INTRO_MESSAGE.LABEL'
                      | translate)
                "
                [required]="isCreateMode()"
                [placeholder]="
                  'ASSISTANTS.SAVE_MODAL.FORM.INTRO_MESSAGE.PLACEHOLDER'
                    | translate
                "
                [limit]="INTRO_MESSAGE_MAX_LENGTH"
                [showCounter]="false"
                [showError]="shouldShowError(forms[loc].controls.introMessage)"
                [errorMessage]="
                  getErrorMessage(forms[loc].controls.introMessage)
                "
              ></sd-textarea>
            }
          }
        </div>
      }
    </div>
  `,
})
export class AssistantBuilderBasicFormComponent {
  /** Active authoring locales; primary first. `['fr']` (OFF) or `['fr','en']` (ON). */
  public readonly locales = input.required<BuilderLocale[]>();
  public readonly hasIntroMessage = input<boolean>(false);
  /** Per-locale effective description display ceiling (T5/KD-7). */
  public readonly descriptionMaxLengthByLocale = input<
    Partial<Record<BuilderLocale, number>>
  >({});
  /** Per-locale stored (original) description, for grandfather-on-edit (KD-7/FM-3). */
  public readonly descriptionStoredValueByLocale = input<
    Partial<Record<BuilderLocale, string | null>>
  >({});
  /** Create-mode: intro required (min 2). Edit keeps the grandfathered optional. */
  public readonly isCreateMode = input<boolean>(false);

  public readonly fromAssistantIcon = fromAssistantIcon;
  private readonly translate = inject(TranslateService);

  /**
   * Stable ids for the per-field group headings (multilingual mode). Each
   * heading names its FR/EN inputs via `aria-labelledby`, so a screen reader
   * announces the field purpose (Name/Description/Intro) alongside the locale
   * tag — the heading is otherwise not programmatically tied to the framed
   * inputs. The shared Icon caption doubles as the icon picker's accessible name.
   */
  private readonly labelIdPrefix = `sd-basic-form-${nextUniqueId++}`;
  protected readonly nameLabelId = `${this.labelIdPrefix}-name`;
  protected readonly descriptionLabelId = `${this.labelIdPrefix}-description`;
  protected readonly introLabelId = `${this.labelIdPrefix}-intro`;
  protected readonly imageLabelId = `${this.labelIdPrefix}-image`;

  // Local state
  public readonly isLogoDropdownVisible = signal<boolean>(false);

  /**
   * Where the icon panel floats relative to the 80px trigger field.
   *
   * Right-aligned to the trigger and below it, falling back to above when there
   * is no room underneath. The CDK does the flipping; the point of the overlay
   * is that neither position is clipped by the form column — see the template.
   *
   * ⚠️ `cdkConnectedOverlayPush` is switched on alongside these, and it is NOT
   * on by default. Without it, at a 375px viewport the right-aligned 376px panel
   * landed at x = −22 and its first icon column sat off-screen. Push slides it
   * back inside; the panel is exactly viewport-wide there, which is the width
   * six 48px cells need.
   */
  protected readonly logoOverlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ];

  // Form constants (re-exported for template usage)
  public readonly NAME_MAX_LENGTH = NAME_MAX_LENGTH;
  public readonly INTRO_MESSAGE_MAX_LENGTH = INTRO_MESSAGE_MAX_LENGTH;

  public readonly isMultilingual = computed(() => this.locales().length > 1);

  // One translatable FormGroup per slot (all three always present so the page
  // can read any; inactive slots are disabled by the effect below). The `und`
  // slot is independent from fr/en so toggling multilingual carries nothing
  // (KD-18 / spec §6 no-carry).
  public readonly forms: Record<
    BuilderLocale,
    FormGroup<AssistantBuilderBasicForm>
  > = {
    fr: this.createLocaleForm(),
    en: this.createLocaleForm(),
    und: this.createLocaleForm(),
  };

  // Logo is a single assistant-level value shared across locales (rendered once).
  //
  // The default must be a value the dropdown actually CONTAINS: the field only
  // renders while the control holds something (see the template), so an empty
  // default would hide the control, and a bare `default` would open the panel on
  // a value it no longer offers.
  public readonly logoControl = new FormControl<string>(
    fromAssistantIcon.NEW_SKILL_DEFAULT_LOGO,
    { nonNullable: true, validators: [Validators.required] },
  );

  private createLocaleForm(): FormGroup<AssistantBuilderBasicForm> {
    return new FormGroup<AssistantBuilderBasicForm>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(NAME_MIN_LENGTH),
          Validators.maxLength(NAME_MAX_LENGTH),
        ],
      }),
      description: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.minLength(DESCRIPTION_MIN_LENGTH),
          Validators.maxLength(DESCRIPTION_MAX_LENGTH),
        ],
      }),
      introMessage: new FormControl<string | null>('', {
        validators: [
          minLengthWhenNotEmpty(INTRO_MESSAGE_MIN_LENGTH),
          Validators.maxLength(INTRO_MESSAGE_MAX_LENGTH),
        ],
      }),
    });
  }

  /** The per-slot FormGroup — the page reads/patches these by slot. */
  public form(locale: BuilderLocale): FormGroup<AssistantBuilderBasicForm> {
    return this.forms[locale];
  }

  /**
   * Uppercase locale tag shown in the leading-label segment when multilingual
   * (Figma 25447 — FR/EN, D9). Only fr/en reach here (the `und` slot renders a
   * plain field label, not a tag).
   */
  public displayLocaleTag(locale: BuilderLocale): string {
    return locale.toUpperCase();
  }

  /**
   * SBB-04: the translation-context language for a multilingual slot's
   * placeholder. Only the fr/en slots are wrapped in
   * `squadboxTranslationLanguageContext`, so each renders its placeholder in its
   * OWN language; the und (single-language) slot is never wrapped and keeps the
   * active UI language (this branch is defensive — und never reaches here).
   */
  public placeholderLanguageFor(locale: BuilderLocale): SupportedLanguage {
    // fr/en slots map straight to their own language. The und (single-language)
    // slot is never wrapped in the directive, so this branch is defensive: fall
    // back to the active UI language, narrowed via resolveSupportedLanguage so an
    // unexpected TranslateService value (e.g. 'en-US') can't reach the directive
    // and trigger a missing-translation load — no `as` cast needed.
    return locale === 'und'
      ? resolveSupportedLanguage(
          this.translate.getCurrentLang() ??
            this.translate.getFallbackLang() ??
            'en',
          'en',
        )
      : locale;
  }

  /** Raw values for a locale, with the shared logo merged in (page contract). */
  public getValues(locale: BuilderLocale): AssistantBuilderBasicFormValues {
    const { name, description, introMessage } =
      this.forms[locale].getRawValue();
    return { name, description, introMessage, logo: this.logoControl.value };
  }

  public descriptionMaxFor(locale: BuilderLocale): number {
    return (
      this.descriptionMaxLengthByLocale()[locale] ?? DESCRIPTION_MAX_LENGTH
    );
  }

  constructor() {
    // Enable only the active locales' groups; disable inactive locales so a
    // locale that isn't being authored can't affect validity or be saved.
    effect(() => {
      const active = new Set(this.locales());
      for (const locale of BUILDER_LOCALES) {
        const group = this.forms[locale];
        if (active.has(locale)) {
          group.enable({ emitEvent: false });
        } else {
          group.disable({ emitEvent: false });
        }
      }
    });

    // T5/KD-7: per-locale description held to the create-strict cap, grandfathering
    // only the pristine stored value on edit; intro required on create only.
    effect(() => {
      const isCreate = this.isCreateMode();
      const storedByLocale = this.descriptionStoredValueByLocale();
      for (const locale of BUILDER_LOCALES) {
        const group = this.forms[locale];
        const descriptionControl = group.controls.description;
        descriptionControl.setValidators([
          Validators.required,
          Validators.minLength(DESCRIPTION_MIN_LENGTH),
          grandfatheredDescriptionMaxLength(storedByLocale[locale] ?? null),
        ]);
        descriptionControl.updateValueAndValidity({ emitEvent: false });

        const introMessageControl = group.controls.introMessage;
        introMessageControl.setValidators(
          isCreate
            ? [
                Validators.required,
                Validators.minLength(INTRO_MESSAGE_MIN_LENGTH),
                Validators.maxLength(INTRO_MESSAGE_MAX_LENGTH),
              ]
            : [
                minLengthWhenNotEmpty(INTRO_MESSAGE_MIN_LENGTH),
                Validators.maxLength(INTRO_MESSAGE_MAX_LENGTH),
              ],
        );
        introMessageControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  public shouldShowError(control: FormControl): boolean {
    return !!(control.invalid && (control.dirty || control.touched));
  }

  public getErrorMessage(control: FormControl): string | undefined {
    return getFormErrorMessage(control, this.translate) || undefined;
  }

  // Event handlers
  public handleClickToggleLogoDropdownCTA(): void {
    this.isLogoDropdownVisible.set(!this.isLogoDropdownVisible());
  }

  /**
   * Escape closes the ICON PANEL, and must not reach the modal behind it.
   *
   * ⚠️ Measured in the browser 2026-09-02: without this, Escape closed the whole
   * Skill Builder and threw away the user's edits. The CDK overlay renders the
   * panel at body level, but FOCUS stays on this trigger — which is inside the
   * modal — so the keydown bubbles straight into `sd-modal`'s own
   * `(keydown.escape)`, which cancels the modal. The overlay closing at the same
   * time hid the damage: the panel did disappear, along with everything else.
   *
   * When the panel is shut this does nothing, so Escape still closes the modal
   * exactly as it always has.
   */
  public handleLogoTriggerEscape(event: Event): void {
    if (!this.isLogoDropdownVisible()) {
      return;
    }
    this.isLogoDropdownVisible.set(false);
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * Escape pressed while focus is INSIDE the panel — a different event entirely
   * from the one above.
   *
   * ⚠️ The two handlers are not redundant, and neither covers the other's case.
   * The overlay renders at body level, so a keydown from a grid cell never
   * bubbles through the trigger and `handleLogoTriggerEscape` cannot see it. The
   * CDK does close the panel on its own — but the focused cell is destroyed with
   * it and focus falls to `<body>`, stranding a keyboard user at the top of the
   * document. Returning focus to the trigger is what
   * [#1450](https://github.com/Insyders/squadbox-app/issues/1450) actually asks
   * for, and this is the half that delivers it "from anywhere in the grid".
   */
  public handleLogoOverlayKeydown(
    event: KeyboardEvent,
    trigger: HTMLElement,
  ): void {
    if (event.key !== 'Escape' || !this.isLogoDropdownVisible()) {
      return;
    }
    this.isLogoDropdownVisible.set(false);
    trigger.focus();
    event.stopPropagation();
    event.preventDefault();
  }

  public handleLogoClick(logo: string): void {
    this.isLogoDropdownVisible.set(false);
    this.logoControl.setValue(logo);
    this.logoControl.markAsDirty();
  }

  public handleLogoDropdownOutsideClick(clickedOutside: boolean): void {
    if (clickedOutside && this.isLogoDropdownVisible()) {
      this.isLogoDropdownVisible.set(false);
    }
  }
}
